import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtInt, orderCupsCount, orderMatchesSearch } from "@/lib/api";
import { useCanAdmin } from "@/lib/useCanAdmin";
import { MagnifyingGlass, ArrowsClockwise, ArrowLeft, Archive, ArrowsCounterClockwise, PencilSimple, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("pt-BR") : "—");

function DaysConfig({ days, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(days);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = parseInt(value, 10);
    if (!n || n < 1 || n > 365) {
      toast.error("Informe um prazo entre 1 e 365 dias");
      return;
    }
    setSaving(true);
    try {
      await api.put("/orders/archive-config", { days: n });
      toast.success(`Arquivamento automático: ${n} dias em Enviado`);
      onSaved(n);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar o prazo");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 px-2 py-1 hover:border-black transition-colors"
        onClick={() => { setValue(days); setEditing(true); }}
        data-testid="archived-days-edit"
        title="Alterar após quantos dias em Enviado o pedido é arquivado automaticamente"
      >
        <PencilSimple size={11} weight="bold" /> Prazo: {days} dias
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1.5" data-testid="archived-days-editor">
      <input
        type="number"
        min={1}
        max={365}
        className="input !py-1 !px-2 text-xs w-20 font-mono"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid="archived-days-input"
        autoFocus
      />
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">dias</span>
      <button className="btn !py-1 !px-2 text-xs" onClick={save} disabled={saving} data-testid="archived-days-save">
        <Check size={12} weight="bold" />
      </button>
      <button className="btn btn-ghost !py-1 !px-2 text-xs" onClick={() => setEditing(false)} data-testid="archived-days-cancel">
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}

export default function ArchivedPanel() {
  const navigate = useNavigate();
  const { canAdmin } = useCanAdmin();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(50);
  const [days, setDays] = useState(30);
  const [restoringId, setRestoringId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders/board/archived");
      setOrders(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      console.warn("archived load:", e?.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/orders/archive-config")
      .then(({ data }) => setDays(data?.days || 30))
      .catch((e) => console.warn("archive-config load:", e?.message));
  }, []);

  const restore = async (o) => {
    if (!window.confirm(`Restaurar o pedido #${o.bling_number} (${o.customer})?\n\nEle volta para a coluna Enviado do Fluxo de Produção e só será arquivado de novo após ${days} dias.`)) return;
    setRestoringId(o.id);
    try {
      await api.post(`/orders/${o.id}/unarchive`);
      toast.success(`Pedido #${o.bling_number} restaurado para o fluxo (Enviado)`);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao restaurar o pedido");
    } finally {
      setRestoringId(null);
    }
  };

  const fullList = orders.filter((o) => orderMatchesSearch(o, search));
  const list = fullList.slice(0, visibleCount);
  const remaining = fullList.length - list.length;

  return (
    <div className="p-3 sm:p-5 md:p-8 max-w-3xl mx-auto" data-testid="archived-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="overline text-zinc-500">FLUXO DE PRODUÇÃO</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tighter flex items-center gap-2">
            <Archive size={28} weight="bold" /> Arquivados
          </h1>
          <div className="text-xs text-zinc-500 mt-1" data-testid="archived-total">
            {total} pedido{total !== 1 ? "s" : ""} enviado{total !== 1 ? "s" : ""} arquivado{total !== 1 ? "s" : ""} (automático após {days} dias em Enviado)
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canAdmin && <DaysConfig days={days} onSaved={setDays} />}
          <button className="btn btn-ghost !py-2 !px-2.5" onClick={() => navigate("/kanban")} data-testid="archived-back-kanban" title="Voltar ao Fluxo de Produção">
            <ArrowLeft size={16} weight="bold" />
          </button>
          <button className="btn btn-ghost !py-2 !px-2.5" onClick={load} data-testid="archived-refresh">
            <ArrowsClockwise size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          className="input pl-8 !py-2 text-xs"
          placeholder="Buscar arquivados: nº, cliente, contato ou pedido na loja"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="archived-search"
        />
      </div>

      {loading ? (
        <div className="text-zinc-400 py-10 text-center">Carregando…</div>
      ) : list.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center text-zinc-400" data-testid="archived-empty">
          {search.trim() ? "Nenhum pedido arquivado encontrado para a busca." : "Nenhum pedido arquivado ainda."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="archived-order-list">
          {list.map((o) => (
            <div key={o.id} className="panel-card p-4" data-testid={`archived-card-${o.bling_number}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <button className="font-mono text-2xl font-bold underline-offset-4 hover:underline" onClick={() => navigate(`/orders/${o.id}`)} data-testid={`archived-open-${o.bling_number}`}>
                      #{o.bling_number}
                    </button>
                    <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-black text-white px-2 py-1">
                      <Archive size={11} weight="fill" /> Arquivado
                    </span>
                  </div>
                  <div className="text-sm font-semibold truncate">{o.customer}</div>
                  <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span><strong className="text-black">{fmtInt(orderCupsCount(o))}</strong> copos</span>
                    <span>silk: <strong className="text-black">{o.silk_color || "—"}</strong></span>
                    {o.archived_at && <span>arquivado em: <strong className="text-black">{fmtDate(o.archived_at)}</strong></span>}
                  </div>
                  {o.archive_reason && (
                    <div className="text-[10px] text-zinc-400 mt-1 uppercase tracking-wide">{o.archive_reason}</div>
                  )}
                </div>
                {canAdmin && (
                  <button
                    className="btn btn-ghost !py-2 !px-3 text-xs shrink-0"
                    onClick={() => restore(o)}
                    disabled={restoringId === o.id}
                    data-testid={`archived-restore-${o.bling_number}`}
                    title="Devolver o pedido à coluna Enviado do Fluxo de Produção"
                  >
                    <ArrowsCounterClockwise size={14} weight="bold" />
                    {restoringId === o.id ? "Restaurando..." : "Restaurar"}
                  </button>
                )}
              </div>
            </div>
          ))}
          {remaining > 0 && (
            <button
              className="w-full py-3 border border-dashed border-zinc-400 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white hover:border-black transition-colors"
              onClick={() => setVisibleCount((v) => v + 50)}
              data-testid="archived-show-more"
            >
              Mostrar mais ({remaining} restantes)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
